import React from 'react';
import { API_BASE_URL, getAuthHeaders } from './api';
// src/services/exchangeRateApi.js - COMPLETO

// Claves de localStorage
const STORAGE_KEY_RATE = 'factTech_exchangeRate';
const STORAGE_KEY_MODE = 'factTech_exchangeRateMode'; // 'auto' o 'manual'
const STORAGE_KEY_RATES = 'factTech_exchangeRates'; // Para guardar todas las tasas como JSON

// Sin tasa disponible no se inventa ninguna: quien consuma este servicio decide
// qué mostrar. Antes había un DEFAULT_RATE de 66 que, con la tasa real cerca de
// 780, facturaba con un error de más del mil por ciento si la fuente fallaba.
const DEFAULT_RATE = null;

// Evento personalizado para notificar cambios en la tasa
const RATE_CHANGE_EVENT = 'factTech_rateChange';

// Función para disparar evento de cambio de tasa
const notifyRateChange = (rate, mode) => {
  // Crear y disparar evento personalizado
  const event = new CustomEvent(RATE_CHANGE_EVENT, { 
    detail: { rate, mode } 
  });
  window.dispatchEvent(event);
  console.log('Evento de cambio de tasa disparado:', rate, mode);
};

/**
 * Trae la tasa desde el backend, que la resuelve contra el BCV.
 *
 * Antes esto llamaba directamente a una API de mercado internacional, guardaba
 * el resultado bajo el nombre "bcv" aunque no fuera la oficial, e inventaba las
 * otras dos multiplicando por 1,02 y 1,01. Como cada navegador consultaba por su
 * cuenta y cacheaba en localStorage, dos personas de la misma empresa podían
 * estar facturando con tasas distintas.
 */
const fetchRatesFromAPI = async () => {
  try {
    const respuesta = await fetch(`${API_BASE_URL}/exchange-rate`, {
      headers: getAuthHeaders()
    });
    if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
    const data = await respuesta.json();
    if (!data.success || !Number.isFinite(Number(data.tasa))) {
      throw new Error(data.message || 'respuesta sin tasa');
    }

    const oficial = Number(data.tasa);
    // Una sola tasa, la oficial. Las variantes que había acá no venían de ningún
    // lado: eran multiplicaciones sobre este mismo número.
    const rates = { bcv: oficial, fuente: data.fuente, fecha: data.fecha };

    localStorage.setItem(STORAGE_KEY_RATES, JSON.stringify(rates));
    return rates;
  } catch (error) {
    console.error('Error al obtener la tasa desde el servidor:', error);

    // Última tasa conocida en este navegador. Es vieja, pero es real: es preferible
    // a un valor por defecto inventado como los 40 y 66 que había antes.
    const guardadas = localStorage.getItem(STORAGE_KEY_RATES);
    if (guardadas) {
      try { return JSON.parse(guardadas); } catch { /* cae al return de abajo */ }
    }
    return null;
  }
};

const exchangeRateApi = {
  // Obtener todas las tasas de cambio
  getExchangeRates: async () => {
    try {
      // Verificar el modo actual
      const mode = localStorage.getItem(STORAGE_KEY_MODE) || 'auto';
      console.log('Modo de tasa de cambio:', mode);
      
      // Si el modo es manual, obtener las tasas manuales
      if (mode === 'manual') {
        const savedRates = localStorage.getItem(STORAGE_KEY_RATES);
        if (savedRates) {
          const rates = JSON.parse(savedRates);
          console.log('Usando tasas manuales:', rates);
          return rates;
        }
      }
      
      // Si estamos en modo automático o no hay tasas manuales, obtener de la API
      if (mode === 'auto' || !localStorage.getItem(STORAGE_KEY_RATES)) {
        console.log('Obteniendo tasas desde API...');
        const rates = await fetchRatesFromAPI();
        return rates;
      }
      
      // Si llegamos aquí, algo falló con el modo
      // Intentar usar tasas guardadas como respaldo
      const savedRates = localStorage.getItem(STORAGE_KEY_RATES);
      if (savedRates) {
        const rates = JSON.parse(savedRates);
        console.log('Usando tasas guardadas anteriormente:', rates);
        return rates;
      }
      
      // Sin fuente ni tasa guardada no se devuelve nada: los 40 que había acá
      // eran un número inventado, y facturar con él estando la real cerca de 780
      // deja el documento mal por más del mil por ciento.
      console.warn('Sin tasa de cambio disponible');
      return null;
    } catch (error) {
      console.error('Error al obtener tasas de cambio:', error);
      
      // Mismo criterio: sin tasa se avisa, no se inventa.
      return null;
    }
  },
  
  // Guardar una tasa manual y cambiar al modo manual
  setManualRate: (rate) => {
    try {
      // Verificar si el valor es realmente diferente
      const currentRate = localStorage.getItem(STORAGE_KEY_RATE);
      if (currentRate && Math.abs(parseFloat(currentRate) - rate) < 0.01) {
        console.log('Ignorando cambio redundante de tasa, ya es similar');
        return {
          rate: rate,
          mode: 'manual',
          success: true
        };
      }
      
      console.log('Guardando tasa manual:', rate);
      localStorage.setItem(STORAGE_KEY_RATE, rate.toString());
      localStorage.setItem(STORAGE_KEY_MODE, 'manual');
      console.log('Modo cambiado a manual');
      
      // Notificar cambio a todos los componentes
      notifyRateChange(rate, 'manual');
      
      return { 
        rate: rate,
        mode: 'manual',
        success: true
      };
    } catch (error) {
      console.error('Error al guardar tasa manual:', error);
      throw error;
    }
  },
  
  // Cambiar al modo automático
  switchToAutoMode: async () => {
    try {
      console.log('Cambiando a modo automático...');
      localStorage.setItem(STORAGE_KEY_MODE, 'auto');
      
      // Obtener nuevas tasas automáticas
      const rates = await fetchRatesFromAPI();
      const rate = rates ? rates.bcv : null;

      // Puede no haber tasa: desde que se sacó el valor por defecto inventado,
      // `fetchRatesFromAPI` devuelve null si ninguna fuente responde. Llamar
      // .toString() sobre eso tiraba un TypeError y volver a modo automático
      // fallaba justo cuando más se lo necesitaba, sin conexión.
      if (rate === null) {
        notifyRateChange(null, 'auto');
        return { rates: null, rate: null, mode: 'auto', success: false };
      }

      // Guardar también la tasa actual para rápido acceso
      localStorage.setItem(STORAGE_KEY_RATE, rate.toString());
      
      // Notificar cambio a todos los componentes
      notifyRateChange(rate, 'auto');
      
      return {
        rates,
        rate,
        mode: 'auto',
        success: true
      };
    } catch (error) {
      console.error('Error al cambiar a modo automático:', error);
      throw error;
    }
  },
  
  // Obtener el modo actual
  getCurrentMode: () => {
    return localStorage.getItem(STORAGE_KEY_MODE) || 'auto';
  },
  
  // Obtener tasa seleccionada actual (para compatibilidad)
  getCurrentRate: async () => {
    try {
      const mode = localStorage.getItem(STORAGE_KEY_MODE) || 'auto';
      const savedType = localStorage.getItem('factTech_selectedRateType') || 'average';
      
      if (mode === 'manual') {
        // Si estamos en modo manual, obtener la tasa guardada
        const rate = localStorage.getItem(STORAGE_KEY_RATE);
        if (rate) {
          return {
            rate: parseFloat(rate),
            mode: 'manual',
            source: 'localStorage'
          };
        }
      }
      
      // Si estamos en modo automático o no hay tasa manual guardada
      // Obtener todas las tasas
      const rates = await exchangeRateApi.getExchangeRates();
      
      // Sólo hay una tasa, la oficial. savedType queda por compatibilidad con
      // preferencias viejas guardadas en el navegador.
      const rate = rates ? (rates[savedType] ?? rates.bcv ?? null) : null;

      if (rate === null || rate === undefined) {
        return { rate: null, mode: 'sin-tasa', source: 'ninguna' };
      }

      localStorage.setItem(STORAGE_KEY_RATE, rate.toString());

      return {
        rate,
        mode: mode,
        source: mode === 'auto' ? 'bcv' : 'manual'
      };
    } catch (error) {
      console.error('Error al obtener tasa actual:', error);
      // Sin tasa se avisa; no se devuelve un número inventado.
      return { rate: null, mode: 'sin-tasa', source: 'ninguna' };
    }
  },
  
  // Escuchar cambios en la tasa (para componentes)
  subscribeToRateChanges: (callback) => {
    window.addEventListener(RATE_CHANGE_EVENT, (event) => {
      callback(event.detail.rate, event.detail.mode);
    });
  },
  
  // Dejar de escuchar (importante para evitar memory leaks)
  unsubscribeFromRateChanges: (callback) => {
    window.removeEventListener(RATE_CHANGE_EVENT, callback);
  }
};

export default exchangeRateApi;